import { FC, ReactNode, useMemo } from 'react';
import {
  QueryStatus,
  QueryTicketDto,
} from '@restore/queries/queries.types';
import { Divider } from 'antd';
import { d7Format } from '@dynamika-shared/utils/date';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import type { SiteType } from '@dynamika-shared/types/site';
import {
  findQueryParticipant,
  formatParticipantName,
} from '@components/Queries/queryParticipants';

const queryInfoRaw = (
  key: string,
  value: string | ReactNode,
  hideDivider?: boolean
) => (
  <>
    <div className='flex justify-between gap-5 py-[13px]'>
      <span className='text-1624 text-dimGray'>{key}</span>
      <span className='text-1624 font-medium text-end'>{value}</span>
    </div>
    {!hideDivider && <Divider className='my-[13px] bg-brightGray' />}
  </>
);

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return 'N/A';
  return d7Format(dateStr, 'dd-LLL-yyyy');
};

type QueryInfoProps = {
  query: QueryTicketDto | null;
};

export const QueryInfo: FC<QueryInfoProps> = ({ query }) => {
  const contextData = useClinicalTrialContextSlice(state => state.data);

  const siteCode = useMemo(() => {
    if (!query?.siteId || !contextData?.configuration?.sites) {
      return 'N/A';
    }
    return (
      contextData.configuration.sites.find(
        (s: SiteType) => s.id === query.siteId
      )?.code ?? 'N/A'
    );
  }, [contextData?.configuration?.sites, query?.siteId]);

  const openedBy = useMemo(() => {
    if (!query?.author) return 'N/A';
    return (
      [query.author.firstName, query.author.lastName]
        .filter(Boolean)
        .join(' ') || query.author.id.slice(0, 5)
    );
  }, [query?.author]);

  const { resolvedBy, resolvedDate } = useMemo(() => {
    if (!query?.closed) {
      return { resolvedBy: 'N/A', resolvedDate: 'N/A' };
    }

    const closeMessages = query.messages.filter(
      m => m.targetStatus === QueryStatus.CLOSED
    );

    if (!closeMessages.length) {
      return { resolvedBy: 'N/A', resolvedDate: 'N/A' };
    }

    const lastClose = closeMessages.at(-1)!;

    const resolver = findQueryParticipant(query, lastClose.actedBy);

    return {
      resolvedBy: formatParticipantName(resolver),
      resolvedDate: formatDate(query.closed),
    };
  }, [query]);

  return (
    <div className='min-w-[448px] w-[30%] h-full flex flex-col border border-brightGray rounded-[12px] bg-white'>
      <h3 className='m-3 text-1824 font-medium p-1.5 border border-brightGray bg-body rounded-[8px] mb-[13px]'>
        Additional Information
      </h3>
      <div className='p-3 body-info flex-1 overflow-y-auto'>
        {queryInfoRaw('Study', contextData?.configuration?.name ?? 'N/A')}
        {queryInfoRaw('Site ID', siteCode)}
        {queryInfoRaw('Subject', query?.patientCode ?? 'N/A')}
        {queryInfoRaw('Date of Birth', query?.patientDob ?? 'N/A')}
        {queryInfoRaw('Visit', query?.visitName ?? 'N/A')}
        {queryInfoRaw('Cohort', query?.cohortName ?? 'N/A')}
        {queryInfoRaw('Query Reason', query?.types?.join(', ') ?? 'N/A')}
        {queryInfoRaw('Opened by', openedBy)}
        {queryInfoRaw('Open Date', formatDate(query?.created))}
        {queryInfoRaw('Resolved by', resolvedBy)}
        {queryInfoRaw('Resolved Date', resolvedDate, true)}
      </div>
    </div>
  );
};
